import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useGetHomeQuery } from "@/store/rest_api";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle } from "lucide-react";
import { MembershipRole } from "@/enum/MembershipRole";
import { MembershipStatus } from "@/enum/MembershipStatus";

const EXEMPT_PATHS = [
  "/manage-organisation/plans",
  "/manage-organisation/billing",
  "/account",
  "/logout",
  "/payment-success",
  "/payment-failed",
  "/manage-organisation/settings/export",
];

function isExempt(pathname: string): boolean {
  return EXEMPT_PATHS.some((path) => pathname === path || pathname.startsWith(path + "/"));
}

interface GateContent {
  title: string;
  message: string;
}

function gateContentFor(status: string | undefined, trialExpired: boolean): GateContent | null {
  if (status === "TRIAL_LAPSED" || (status === "TRIAL" && trialExpired)) {
    return {
      title: "Your trial has ended",
      message:
        "Subscribe to a plan to continue using the platform. Your data is safe and will be restored as soon as you subscribe.",
    };
  }
  if (status === "CANCELED") {
    return {
      title: "Subscription canceled",
      message: "Your subscription is no longer active. Choose a plan to restore access. Your data is safe.",
    };
  }
  if (status === "SUSPENDED") {
    return {
      title: "Subscription inactive",
      message: "We could not process your latest payment. Update your billing details to restore access.",
    };
  }
  return null;
}

export default function SubscriptionGate() {
  const { data: user } = useGetHomeQuery();
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const organisation = user?.activeOrganisation;
  const trialExpired =
    !!organisation?.trialExpirationDate && new Date(organisation.trialExpirationDate) < new Date();
  const content = gateContentFor(organisation?.subscriptionStatus, trialExpired);
  const exportAvailableUntil = organisation?.exportAvailableUntil;
  const canExportWorkspace =
    user?.activeMembership?.role === MembershipRole.OWNER && user?.activeMembership?.status === MembershipStatus.ACTIVE;

  if (!content || isExempt(location.pathname)) {
    return <Outlet />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4 text-foreground">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            <CardTitle>{content.title}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">{content.message}</p>
          {canExportWorkspace && exportAvailableUntil && (
            <p className="text-sm text-muted-foreground">
              You can export all your data until {new Date(exportAvailableUntil).toLocaleString()}.
            </p>
          )}
          <div className="flex flex-col gap-2">
            <Button onClick={() => navigate("/manage-organisation/plans")} className="w-full">
              Choose a plan
            </Button>
            {canExportWorkspace && exportAvailableUntil && (
              <Button variant="outline" onClick={() => navigate("/manage-organisation/settings/export")} className="w-full">
                Export your data
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate("/manage-organisation/billing/overview")}
              className="w-full"
            >
              Billing settings
            </Button>
            <Button variant="ghost" onClick={() => logout()} className="w-full">
              Log out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
